import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  makeStyles, tokens,
  Body1, Body2, Caption1, Button,
} from '@fluentui/react-components';
import { useAuth } from '../hooks/useAuth';
import { FullPageSpinner } from '../components/common/FullPageSpinner';
import { brandColors } from '../theme/brandColors';

/* Branded two-column sign-in page. Mirrors the layout the CustomerSuccess Portal
   uses so the Transformr product family reads as one. Hero font sizes, paddings
   in px, and gradient colour stops are deliberate — they describe brand identity
   that the Fluent token system can't express. */

const useStyles = makeStyles({
  page: { display: 'flex', minHeight: '100vh' },

  brandPanel: {
    flex: '1 1 55%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '64px 48px',
    background: `linear-gradient(135deg, ${brandColors.darkBlue} 0%, ${brandColors.blue} 60%, ${brandColors.lightBlue} 100%)`,
    color: brandColors.white,
    position: 'relative',
    overflow: 'hidden',
    '@media (max-width: 768px)': { display: 'none' },
  },
  brandContent: {
    position: 'relative',
    zIndex: 1,
    maxWidth: '480px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  brandLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
  },
  logoImg: { height: '56px', objectFit: 'contain', filter: 'brightness(0) invert(1)' },
  brandTitle: {
    fontSize: tokens.fontSizeHero1000,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightHero1000,
    margin: 0,
  },
  brandTagline: {
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    opacity: 0.85,
    margin: 0,
  },
  brandFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalL,
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase300,
    opacity: 0.9,
  },
  featureDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: brandColors.green,
    flexShrink: 0,
  },
  bgCircle1: {
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.04)',
    top: '-100px',
    right: '-150px',
  },
  bgCircle2: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.03)',
    bottom: '-50px',
    left: '-80px',
  },

  loginPanel: {
    flex: '1 1 45%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 40px',
    backgroundColor: brandColors.white,
    '@media (max-width: 768px)': { flex: '1 1 100%' },
  },
  loginCard: {
    maxWidth: '380px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXXL,
  },
  loginHeader: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  welcomeText: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    color: brandColors.darkGrey,
    margin: 0,
  },
  loginBody: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL },
  description: { color: tokens.colorNeutralForeground2 },
  error: {
    color: tokens.colorPaletteRedForeground1,
    padding: '12px 16px',
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  signInButton: {
    width: '100%',
    height: '44px',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    textAlign: 'center',
  },
});

const FEATURES = [
  'Per-project Epic, tree and velocity reports',
  'Programme dashboards: burnup, WSJF, value-at-risk',
  'Customer-friendly roadmaps and decisions tracker',
] as const;

export function LoginPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isReady, loginRedirect } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const returnPath = (location.state as { from?: string } | null)?.from ?? '/';

  // Already signed in? Bounce them straight to wherever they were headed.
  useEffect(() => {
    if (isReady && isAuthenticated) {
      navigate(returnPath, { replace: true });
    }
  }, [isReady, isAuthenticated, navigate, returnPath]);

  const handleLogin = async () => {
    try {
      setError(null);
      await loginRedirect();
    } catch (err) {
      setError('Sign in failed. Please try again.');
      console.error('Login error:', err);
    }
  };

  if (!isReady) {
    return <FullPageSpinner label="Checking your session…" />;
  }

  return (
    <div className={styles.page}>
      {/* Left brand panel */}
      <div className={styles.brandPanel}>
        <div className={styles.bgCircle1} aria-hidden />
        <div className={styles.bgCircle2} aria-hidden />
        <div className={styles.brandContent}>
          <div className={styles.brandLogo}>
            <img src="/transformr-logo.png" alt="Transformr" className={styles.logoImg} />
          </div>
          <h1 className={styles.brandTitle}>DevOps Reports</h1>
          <p className={styles.brandTagline}>
            Customer-facing reports surfaced live from Azure DevOps — per project, across the programme, and built for sponsors.
          </p>
          <div className={styles.brandFeatures}>
            {FEATURES.map((f) => (
              <div key={f} className={styles.feature}>
                <div className={styles.featureDot} aria-hidden />
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className={styles.loginPanel}>
        <div className={styles.loginCard}>
          <div className={styles.loginHeader}>
            <h2 className={styles.welcomeText}>Welcome</h2>
            <Body1 className={styles.description}>
              Sign in to your DevOps Reports workspace.
            </Body1>
          </div>

          <div className={styles.loginBody}>
            {error && (
              <Body2 className={styles.error}>{error}</Body2>
            )}

            <Button
              appearance="primary"
              size="large"
              className={styles.signInButton}
              onClick={handleLogin}
            >
              Sign in with Microsoft 365
            </Button>
          </div>

          <div className={styles.footer}>
            <Caption1 className={styles.description}>
              Secure authentication powered by Microsoft Entra ID.
            </Caption1>
            <Caption1 className={styles.description}>
              By signing in, you agree to Transformr's terms of service and privacy policy.
            </Caption1>
          </div>
        </div>
      </div>
    </div>
  );
}
